import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Offcanvas, Form, Row, Col } from 'react-bootstrap';
import $ from 'jquery';
import 'datatables.net-bs5';
import "datatables.net-responsive/js/dataTables.responsive.min.js";
import { useHasPermission, PERMISSIONS } from '../../utils/permissions';
import PermissionGate from '../common/PermissionGate';
import { toast } from 'react-toastify';

function PurchaseOrderTable({ orders, canEdit = false, canDelete = false, canView = true }) {
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        vendor: '',
        broker: '',
        status: '',
        paymentStatus: '',
        dateFrom: '',
        dateTo: ''
    });
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [brokers, setBrokers] = useState([]);

    // Additional permission checks
    const canViewDocuments = useHasPermission(PERMISSIONS.PO_DOC_ADD);
    const canViewPayments = useHasPermission(PERMISSIONS.PO_PAYMENT_ADD);
    const canViewGRN = useHasPermission(PERMISSIONS.GRN_CREATE);

    useEffect(() => {
        if (orders && orders.length > 0) {
            setFilteredOrders(orders);
            const uniqueVendors = orders.filter(o => o.vendorId).map(o => o.vendorId);
            const uniqueBrokers = orders.filter(o => o.brokerId).map(o => o.brokerId);
            setVendors([...new Map(uniqueVendors.map(v => [v._id, v])).values()]);
            setBrokers([...new Map(uniqueBrokers.map(b => [b._id, b])).values()]);
        }
    }, [orders]);

    useEffect(() => {
        if (filteredOrders && filteredOrders.length >= 0) {
            const $table = $(tableRef.current);

            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }

            setTimeout(() => {
                dataTableRef.current = $table.DataTable({
                    responsive: true,
                    destroy: true,
                    data: filteredOrders,
                    columns: [
                        { 
                            data: null,
                            render: (data, type, row, meta) => meta.row + 1,
                            title: "SN",
                            width: "50px"
                        },
                        { 
                            data: null,
                            render: (data, type, row, meta) => {
                                let displayData = row.orderId || `Pending`;
                                let html = `<a href="/purchase-orders/${row._id}">${displayData}</a>`;
                                return html;
                            }
                        },
                        { 
                            data: 'orderDate',
                            title: "Order Date",
                            render: (data) => new Date(data).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                            })
                        },
                        { 
                            data: 'items',
                            title: "Items",
                            render: (data) => {
                                if (!data || data.length === 0) return 'No items';
                                return data.map(item => 
                                    `${item.productId?.productName || 'N/A'} (${item.orderedQuantity} ${item.unit})`
                                ).join('<br>');
                            },
                            orderable: false
                        },
                        { 
                            data: null,
                            title: "Vendor",
                            render: (data, type, row) => {
                                let html = `<div>${row.vendorId?.business_name || 'N/A'}</div>`;
                                if (row.brokerId?.business_name) {
                                    html += `<small class="text-muted">via: ${row.brokerId.business_name}</small>`;
                                }
                                return html;
                            }
                        },
                        { 
                            data: 'status',
                            title: "Status",
                            render: (data) => `<span class="badge text-bg-${getOrderStatusColor(data)}">${data}</span>`
                        },
                        { 
                            data: 'paymentStatus',
                            title: "Payment",
                            render: (data) => `<span class="badge text-bg-${getPaymentStatusColor(data)}">${data}</span>`
                        },
                        { 
                            data: '_id',
                            title: "Actions",
                            render: (data, type, row) => {
                                let actions = '';
                                
                                if (canView) {
                                    actions += `<a class="btn btn-sm btn-info me-1" href="/purchase-orders/${data}" title="View Details">
                                        <i class="fas fa-eye"></i>
                                    </a>`;
                                }
                                
                                // Only show edit if it's in Draft or if the user has permission for other states
                                if (canEdit && (row.status === 'Draft' || row.status === 'Approved' || row.status === 'Created')) {
                                    actions += `<a class="btn btn-sm btn-warning me-1" href="/edit-purchase-order/${data}" title="Edit Order">
                                        <i class="fas fa-edit"></i>
                                    </a>`;
                                }

                                // Action dropdown for additional permissions
                                const hasAdditionalActions = (canViewDocuments || canViewPayments || canViewGRN) && row.status !== 'Draft' && row.status !== 'Cancelled';
                                if (hasAdditionalActions) {
                                    actions += `
                                        <div class="btn-group" role="group">
                                            <button type="button" class="btn btn-sm btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" title="More Actions">
                                                <i class="fas fa-ellipsis-v"></i>
                                            </button>
                                            <ul class="dropdown-menu">`;
                                    
                                    if (canViewGRN) {
                                        actions += `<li><a class="dropdown-item" href="/purchase-orders/${data}?tab=grn">
                                            <i class="fas fa-truck me-2"></i>GRN
                                        </a></li>`;
                                    }
                                    
                                    if (canViewDocuments) {
                                        actions += `<li><a class="dropdown-item" href="/purchase-orders/${data}?tab=documents">
                                            <i class="fas fa-file me-2"></i>Documents
                                        </a></li>`;
                                    }
                                    
                                    if (canViewPayments) {
                                        actions += `<li><a class="dropdown-item" href="/purchase-orders/${data}?tab=payments">
                                            <i class="fas fa-credit-card me-2"></i>Payments
                                        </a></li>`;
                                    }
                                    
                                    actions += `</ul></div>`;
                                }

                                return actions;
                            },
                            className: 'text-end',
                            orderable: false,
                            width: canEdit ? "120px" : "80px"
                        }
                    ],
                    layout: {
                        bottomEnd: {
                            paging: {
                                firstLast: false
                            }
                        }
                    }
                });
            }, 100);
        }

        return () => {
            if (dataTableRef.current) {
                dataTableRef.current.destroy();
                dataTableRef.current = null;
            }
        };
    }, [filteredOrders, canView, canEdit, canDelete, canViewDocuments, canViewPayments, canViewGRN]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const applyFilters = () => {
        let filtered = orders || [];
        
        if (filters.vendor) {
            filtered = filtered.filter(order => order.vendorId?._id === filters.vendor);
        }
        if (filters.broker) {
            filtered = filtered.filter(order => order.brokerId?._id === filters.broker);
        }
        if (filters.status) {
            filtered = filtered.filter(order => order.status === filters.status);
        }
        if (filters.paymentStatus) {
            filtered = filtered.filter(order => order.paymentStatus === filters.paymentStatus);
        }
        if (filters.dateFrom) {
            filtered = filtered.filter(order => 
                new Date(order.orderDate) >= new Date(filters.dateFrom)
            );
        }
        if (filters.dateTo) {
            filtered = filtered.filter(order => 
                new Date(order.orderDate) <= new Date(filters.dateTo)
            );
        }
        
        setFilteredOrders(filtered);
        setShowFilters(false);
    };

    const clearFilters = () => {
        setFilters({
            vendor: '',
            broker: '',
            status: '',
            paymentStatus: '',
            dateFrom: '',
            dateTo: ''
        });
        setFilteredOrders(orders || []);
    };

    const exportToCSV = () => {
        if (!canView) {
            toast.error("You don't have permission to export purchase orders");
            return;
        }

        const csvContent = [
            ['Order No', 'Order Date', 'Vendor', 'Status', 'Payment Status', 'Total Amount'],
            ...filteredOrders.map(order => [
                order.orderId || 'Pending',
                new Date(order.orderDate).toLocaleDateString(),
                order.vendorId?.business_name || 'N/A',
                order.status,
                order.paymentStatus,
                order.itemsTotalPrice || 0
            ])
        ];

        const csvString = csvContent.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (!orders || orders.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p className="text-muted">No purchase orders found.</p>
                <PermissionGate require={PERMISSIONS.PO_CREATE}>
                    <Link to="/new-purchase-order" className="btn btn-primary">
                        <i className="fas fa-plus me-2"></i>Create First Purchase Order
                    </Link>
                </PermissionGate>
            </div>
        );
    }

    return (
        <>
            <div className="px-3 d-flex justify-content-between align-items-center gap-2 mb-3">
                <div className="d-flex align-items-center gap-3">
                    <small className="text-muted">
                        Showing {filteredOrders.length} of {orders?.length || 0} orders
                    </small>
                    {(canEdit || canDelete) && (
                        <div className="d-flex gap-2">
                            {/* <Button 
                                variant="outline-success" 
                                size="sm" 
                                onClick={exportToCSV}
                                title="Export to CSV"
                            >
                                <i className="fas fa-download me-1"></i>Export
                            </Button> */}
                        </div>
                    )}
                </div>
                <Button variant="outline-primary" onClick={() => setShowFilters(true)}>
                    <i className="fas fa-filter me-2"></i>Filters
                </Button>
            </div>
            
            <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filter Purchase Orders</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor</Form.Label>
                            <Form.Select
                                value={filters.vendor}
                                onChange={(e) => handleFilterChange('vendor', e.target.value)}
                            >
                                <option value="">All Vendors</option>
                                {vendors.map(vendor => (
                                    <option key={vendor._id} value={vendor._id}>
                                        {vendor.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Broker</Form.Label>
                            <Form.Select
                                value={filters.broker}
                                onChange={(e) => handleFilterChange('broker', e.target.value)}
                            >
                                <option value="">All Brokers</option>
                                {brokers.map(broker => (
                                    <option key={broker._id} value={broker._id}>
                                        {broker.business_name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="Draft">Draft</option>
                                <option value="Approved">Approved</option>
                                <option value="Created">Created</option>
                                <option value="Partially Delivered">Partially Delivered</option>
                                <option value="Delivered">Delivered</option>
                                <option value="Closed">Closed</option>
                                <option value="Cancelled">Cancelled</option>
                            </Form.Select>
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Payment Status</Form.Label>
                            <Form.Select
                                value={filters.paymentStatus}
                                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                            >
                                <option value="">All Payment Status</option>
                                <option value="Unpaid">Unpaid</option>
                                <option value="Partially Paid">Partially Paid</option>
                                <option value="Paid">Paid</option>
                            </Form.Select>
                        </Form.Group>
                        
                        <Row>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date From</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Date To</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <div className="d-grid gap-2">
                            <Button variant="primary" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                            <Button variant="secondary" onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </div>
                    </Form>
                </Offcanvas.Body>
            </Offcanvas>
            
            <div className="table-responsive">
                <table
                    ref={tableRef}
                    className="table table-bordered table-hover table-sm display responsive nowrap"
                    id="ordersTable"
                    style={{ width: '100%' }}
                >
                    <thead className="table-light">
                        <tr>
                            <th>SN</th>
                            <th>Order No</th>
                            <th>Order Date</th>
                            <th>Items</th>
                            <th>Vendor</th>
                            <th>Status</th>
                            <th>Payment</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                    </tbody>
                </table>
            </div>
        </>
    );
}

function getOrderStatusColor(status) {
    switch (status) {
        case 'Draft':
            return 'secondary';
        case 'Approved':
            return 'info';
        case 'Created':
            return 'warning';
        case 'Partially Delivered':
            return 'info';
        case 'Delivered':
            return 'primary';
        case 'Closed':
            return 'success';
        case 'Cancelled':
            return 'danger';
        default:
            return 'light';
    }
}

function getPaymentStatusColor(status) {
    switch (status) {
        case 'Unpaid':
            return 'danger';
        case 'Partially Paid':
            return 'warning';
        case 'Paid':
            return 'success';
        default:
            return 'light';
    }
}

export default PurchaseOrderTable;